import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { auth } from '@/api/admin';
import { useAuthStore } from '@/auth/store';

/// Sprint 28 — landing page for the OAuth web flow. Backend redirects
/// here as `/auth/callback#accessToken=...&refreshToken=...&isOwner=...`.
/// We parse the fragment, persist tokens, fetch /me, and then route to
/// either the original destination or a "not admin" page.
///
/// The fragment-based delivery (vs query string) keeps tokens out of:
///   - Server access logs (browsers don't forward fragment to network)
///   - Referer headers when the user navigates away
///   - `window.location.search` analytics auto-captures
///
/// This still requires the dashboard to run on a host the backend
/// allowlists via ADMIN_DASHBOARD_URL, so a stolen redirect URL alone
/// can't grab tokens.
export default function AuthCallback() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);
  // Guard against React StrictMode double-mount in dev — without it,
  // setAuth fires twice and /me hits the rate limiter on slow networks.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const accessToken = params.get('accessToken');
    const refreshToken = params.get('refreshToken');
    const oauthError = params.get('error');

    // Sprint 28 — server refuses non-admin web sign-ins by redirecting
    // here with `error=not_admin` and no tokens. Show a generic message
    // (no internal details exposed to a user who shouldn't be here in
    // the first place).
    if (oauthError === 'not_admin') {
      setError(
        'This account does not have admin access. Kindly contact ' +
          'OffCoder support if you believe this is a mistake.',
      );
      return;
    }

    if (!accessToken || !refreshToken) {
      setError(
        oauthError || 'No tokens in the callback URL. Try signing in again.',
      );
      return;
    }

    // Wipe the fragment so a refresh doesn't re-process stale tokens
    // (and so the URL bar doesn't read like a security incident).
    window.history.replaceState({}, '', '/auth/callback');

    (async () => {
      try {
        // Stash tokens with a placeholder user — we re-fetch /me below
        // to populate the real user record. Without this initial set,
        // the auth interceptor can't attach the bearer header to /me.
        setAuth(
          { id: '', email: '', name: '', isAdmin: false } as never,
          accessToken,
          refreshToken,
        );
        const me = await auth.me();
        const dest =
          sessionStorage.getItem('admin.postLoginRedirect') || '/';
        sessionStorage.removeItem('admin.postLoginRedirect');

        if (!me.user.isAdmin) {
          // Save the real user so the "Not admin" screen can show
          // sign-out + email; route there instead of the dashboard.
          setAuth(me.user, accessToken, refreshToken);
          navigate('/not-admin', { replace: true });
          return;
        }
        setAuth(me.user, accessToken, refreshToken);
        navigate(dest, { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Token exchange failed.');
      }
    })();
  }, [navigate, setAuth]);

  if (error) {
    return (
      <div className="flex min-h-full items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-3 text-center">
          <h2 className="text-lg font-semibold text-status-error">
            Sign-in failed
          </h2>
          <p className="text-sm text-text-secondary">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="btn-ghost"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="flex flex-col items-center gap-3 text-text-secondary">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-sm">Signing you in…</span>
      </div>
    </div>
  );
}
